/**
 * MixpanelEvents
 * High-level tracking functions for all user interactions
 */

import mixpanelService from './MixpanelService';
import { MIXPANEL_EVENTS, USER_PROPERTIES, FLOW_IDS } from '../config/mixpanelConfig';

// ============================================
// Session Events
// ============================================

/**
 * Track session start
 * @param {Object} sessionData - Session information
 */
export const trackSessionStart = (sessionData) => {
  mixpanelService.identify(sessionData.sessionId);

  // Set first-time properties
  mixpanelService.setUserPropertyOnce({
    [USER_PROPERTIES.FIRST_SEEN]: new Date().toISOString(),
    [USER_PROPERTIES.INITIAL_LANGUAGE]: sessionData.language,
    [USER_PROPERTIES.INITIAL_REFERRER]: sessionData.referrer || 'direct',
  });

  // Update last seen
  mixpanelService.setUserProperties({
    [USER_PROPERTIES.LAST_SEEN]: new Date().toISOString(),
    [USER_PROPERTIES.PREFERRED_LANGUAGE]: sessionData.language,
  });

  // Increment session count
  mixpanelService.incrementUserProperty(USER_PROPERTIES.SESSION_COUNT);

  // Start timing session end
  mixpanelService.timeEvent(MIXPANEL_EVENTS.SESSION_END);

  // Track the event
  mixpanelService.track(MIXPANEL_EVENTS.SESSION_START, {
    language: sessionData.language,
    referrer: sessionData.referrer || 'direct',
    is_widget: sessionData.isWidget || false,
    has_consent: sessionData.hasConsent,
    user_agent_type: detectDeviceType(),
  });
};

/**
 * Track session end
 * @param {Object} sessionData - Session summary data
 */
export const trackSessionEnd = (sessionData) => {
  mixpanelService.track(MIXPANEL_EVENTS.SESSION_END, {
    duration_seconds: sessionData.durationSeconds,
    messages_sent: sessionData.messageCount,
    flows_completed: sessionData.flowsCompleted,
    explore_more_clicks: sessionData.exploreMoreClicks || 0,
    final_flow: sessionData.lastFlow,
    feedback_given: sessionData.feedbackGiven || false,
  });
};

// ============================================
// Chat Events
// ============================================

/**
 * Track chat opened
 * @param {Object} context - Chat open context
 */
export const trackChatOpened = (context) => {
  // Start timing chat closed
  mixpanelService.timeEvent(MIXPANEL_EVENTS.CHAT_CLOSED);

  mixpanelService.track(MIXPANEL_EVENTS.CHAT_OPENED, {
    source: context.source || 'launcher', // 'launcher', 'auto_open', 'widget'
    language: context.language,
    has_previous_session: context.hasPreviousSession || false,
  });
};

/**
 * Track chat closed
 * @param {Object} context - Chat close context
 */
export const trackChatClosed = (context) => {
  mixpanelService.track(MIXPANEL_EVENTS.CHAT_CLOSED, {
    conversation_depth: context.messageCount || 0,
    active_flow: context.activeFlow,
    had_feedback: context.hadFeedback || false,
  });
};

/**
 * Track message sent by user
 * @param {Object} messageData - Message information
 */
export const trackMessageSent = (messageData) => {
  mixpanelService.incrementUserProperty(USER_PROPERTIES.TOTAL_MESSAGES);

  mixpanelService.track(MIXPANEL_EVENTS.MESSAGE_SENT, {
    message_length: messageData.length,
    language: messageData.language,
    active_flow: messageData.activeFlow,
    conversation_depth: messageData.conversationDepth,
    is_first_message: messageData.conversationDepth === 1,
  });
};

/**
 * Track message received (AI response)
 * @param {Object} responseData - Response information
 */
export const trackMessageReceived = (responseData) => {
  mixpanelService.track(MIXPANEL_EVENTS.MESSAGE_RECEIVED, {
    response_length: responseData.length,
    confidence: responseData.confidence,
    classification: responseData.classification, // 'on_topic', 'off_topic'
    has_buttons: responseData.hasButtons || false,
    has_links: responseData.hasLinks || false,
    is_offline_response: responseData.isOffline || false,
    is_streamed: responseData.isStreamed || false,
    tokens_used: responseData.tokensUsed,
    response_time_ms: responseData.responseTimeMs,
  });
};

// ============================================
// Flow Events
// ============================================

/**
 * Track flow started
 * @param {Object} flowData - Flow information
 */
export const trackFlowStarted = (flowData) => {
  // Start timing for completion and abandonment
  mixpanelService.timeEvent(MIXPANEL_EVENTS.FLOW_COMPLETED);
  mixpanelService.timeEvent(MIXPANEL_EVENTS.FLOW_ABANDONED);

  mixpanelService.track(MIXPANEL_EVENTS.FLOW_STARTED, {
    flow_id: flowData.flowId,
    flow_name: flowData.flowName,
    source: flowData.source || 'quick_action', // 'quick_action', 'explore_more', 'ai_suggestion'
    language: flowData.language,
    previous_flow: flowData.previousFlow,
  });

  // Update user properties based on flow type
  if (flowData.flowId === FLOW_IDS.DONATION || flowData.flowId?.includes('donate')) {
    mixpanelService.setUserProperties({
      [USER_PROPERTIES.HAS_DONATED]: true,
    });
  }

  if (flowData.flowId === FLOW_IDS.GIVEAWAY || flowData.flowId?.includes('giveaway')) {
    mixpanelService.setUserProperties({
      [USER_PROPERTIES.HAS_SEARCHED_GIVEAWAYS]: true,
    });
  }
};

/**
 * Track flow completed
 * @param {Object} flowData - Flow completion data
 */
export const trackFlowCompleted = (flowData) => {
  mixpanelService.incrementUserProperty(USER_PROPERTIES.TOTAL_FLOWS_COMPLETED);

  mixpanelService.track(MIXPANEL_EVENTS.FLOW_COMPLETED, {
    flow_id: flowData.flowId,
    flow_name: flowData.flowName,
    outcome: flowData.outcome || 'success', // 'success', 'escalated', 'redirected'
    messages_in_flow: flowData.messageCount,
    buttons_clicked: flowData.buttonsClicked,
    language: flowData.language,
  });
};

/**
 * Track flow abandoned
 * @param {Object} flowData - Flow abandonment data
 */
export const trackFlowAbandoned = (flowData) => {
  mixpanelService.track(MIXPANEL_EVENTS.FLOW_ABANDONED, {
    flow_id: flowData.flowId,
    flow_name: flowData.flowName,
    abandonment_point: flowData.abandonmentPoint,
    messages_in_flow: flowData.messageCount,
    reason: flowData.reason || 'unknown', // 'timeout', 'close', 'flow_switch', 'return_home'
    language: flowData.language,
  });
};

// ============================================
// Engagement Events
// ============================================

/**
 * Track button clicked
 * @param {Object} buttonData - Button click information
 */
export const trackButtonClicked = (buttonData) => {
  mixpanelService.track(MIXPANEL_EVENTS.BUTTON_CLICKED, {
    button_id: buttonData.buttonId,
    button_label: buttonData.label,
    button_type: buttonData.type || 'quick_action', // 'quick_action', 'follow_up', 'external'
    active_flow: buttonData.activeFlow,
    position: buttonData.position,
    language: buttonData.language,
  });
};

/**
 * Track Explore More clicked
 * @param {Object} exploreData - Explore More context
 */
export const trackExploreMoreClicked = (exploreData) => {
  mixpanelService.track(MIXPANEL_EVENTS.EXPLORE_MORE_CLICKED, {
    current_flow: exploreData.currentFlow,
    conversation_depth: exploreData.conversationDepth,
    options_shown: exploreData.optionsShown,
    trigger: exploreData.trigger || 'user_request', // 'dead_end', 'user_request', 'auto_suggest'
  });
};

/**
 * Track Explore More option selected
 * @param {Object} selectionData - Selection information
 */
export const trackExploreMoreSelected = (selectionData) => {
  mixpanelService.track(MIXPANEL_EVENTS.EXPLORE_MORE_SELECTED, {
    selected_option: selectionData.optionId,
    selected_label: selectionData.label,
    position: selectionData.position,
    is_ai_generated: selectionData.isAIGenerated || false,
    previous_flow: selectionData.previousFlow,
  });

  // Mark as deep explorer once they've explored 3+ times. Mixpanel's
  // increment has no readback, so the threshold is counted locally.
  mixpanelService.incrementUserProperty('explore_more_count');
  try {
    const count = parseInt(localStorage.getItem('compudopt_explore_count') || '0', 10) + 1;
    localStorage.setItem('compudopt_explore_count', String(count));
    if (count >= 3) {
      markAsDeepExplorer();
    }
  } catch {
    // localStorage unavailable - skip the deep-explorer promotion
  }
};

/**
 * Track external link clicked
 * @param {Object} linkData - Link information
 */
export const trackExternalLinkClicked = (linkData) => {
  mixpanelService.track(MIXPANEL_EVENTS.EXTERNAL_LINK_CLICKED, {
    link_category: linkData.category, // 'giveaway', 'donation', 'resource', 'partner'
    link_label: linkData.label,
    active_flow: linkData.activeFlow,
    language: linkData.language,
    // Note: Don't track full URL for privacy
  });
};

/**
 * Track language changed
 * @param {Object} languageData - Language change information
 */
export const trackLanguageChanged = (languageData) => {
  mixpanelService.setUserProperties({
    [USER_PROPERTIES.PREFERRED_LANGUAGE]: languageData.newLanguage,
  });

  mixpanelService.track(MIXPANEL_EVENTS.LANGUAGE_CHANGED, {
    previous_language: languageData.previousLanguage,
    new_language: languageData.newLanguage,
    conversation_depth: languageData.conversationDepth,
  });
};

// ============================================
// Feedback Events
// ============================================

/**
 * Track feedback submitted
 * @param {Object} feedbackData - Feedback information
 */
export const trackFeedbackSubmitted = (feedbackData) => {
  mixpanelService.incrementUserProperty(USER_PROPERTIES.FEEDBACK_GIVEN_COUNT);

  mixpanelService.track(MIXPANEL_EVENTS.FEEDBACK_SUBMITTED, {
    feedback_type: feedbackData.type, // 'helpful', 'not_helpful'
    has_text_feedback: feedbackData.hasTextFeedback || false,
    active_flow: feedbackData.activeFlow,
    conversation_depth: feedbackData.conversationDepth,
    response_classification: feedbackData.classification,
  });
};

/**
 * Track response rated
 * @param {Object} ratingData - Rating information
 */
export const trackResponseRated = (ratingData) => {
  mixpanelService.track(MIXPANEL_EVENTS.RESPONSE_RATED, {
    // feedback_type is the cross-platform key (the desktop app sends the
    // same values) and what the Positive Feedback Rate metric filters on
    feedback_type: ratingData.rating === 'positive' ? 'helpful' : 'not_helpful',
    rating: ratingData.rating, // 'positive', 'negative'
    category: ratingData.category,
    language: ratingData.language,
    is_offline_response: ratingData.isOffline || false,
    response_index: ratingData.responseIndex,
  });
};

/**
 * Track response flagged for review
 * @param {Object} flagData - Flag information
 */
export const trackFlaggedForReview = (flagData) => {
  mixpanelService.track(MIXPANEL_EVENTS.FLAGGED_FOR_REVIEW, {
    reason: flagData.reason, // 'negative_feedback', 'harmful_content', 'inaccurate'
    category: flagData.category,
    language: flagData.language,
  });
};

// ============================================
// Search Events
// ============================================

/**
 * Track zip code searched (privacy-safe)
 * @param {Object} searchData - Search context
 */
export const trackZipCodeSearched = (searchData) => {
  mixpanelService.track(MIXPANEL_EVENTS.ZIP_CODE_SEARCHED, {
    // Note: Never track actual zip code
    source: searchData.source || 'inline', // 'inline', 'dedicated_input'
    active_flow: searchData.activeFlow,
    language: searchData.language,
  });
};

/**
 * Track giveaway results viewed
 * @param {Object} resultsData - Results information
 */
export const trackGiveawayResultsViewed = (resultsData) => {
  mixpanelService.track(MIXPANEL_EVENTS.GIVEAWAY_RESULTS_VIEWED, {
    results_count: resultsData.count,
    has_nearby_events: resultsData.hasNearbyEvents || false,
    distance_category: resultsData.distanceCategory, // 'local', 'regional', 'national'
  });
};

// ============================================
// Widget Events
// ============================================

/**
 * Track widget loaded
 * @param {Object} widgetData - Widget load information
 */
export const trackWidgetLoaded = (widgetData) => {
  mixpanelService.track(MIXPANEL_EVENTS.WIDGET_LOADED, {
    load_time_ms: widgetData.loadTimeMs,
    embedding_domain: widgetData.embeddingDomain,
    version: widgetData.version,
  });
};

/**
 * Track widget error
 * @param {Object} errorData - Error information
 */
export const trackWidgetError = (errorData) => {
  mixpanelService.track(MIXPANEL_EVENTS.WIDGET_ERROR, {
    error_type: errorData.type,
    error_message: errorData.message,
    component: errorData.component,
  });
};

// ============================================
// Admin Events
// ============================================

/**
 * Track admin page viewed
 * @param {Object} pageData - Page view information
 */
export const trackAdminPageViewed = (pageData) => {
  mixpanelService.track(MIXPANEL_EVENTS.ADMIN_PAGE_VIEWED, {
    section: pageData.section,
    previous_section: pageData.previousSection,
  });
};

/**
 * Track admin export clicked
 * @param {Object} exportData - Export information
 */
export const trackAdminExportClicked = (exportData) => {
  mixpanelService.track(MIXPANEL_EVENTS.ADMIN_EXPORT_CLICKED, {
    export_type: exportData.type, // 'csv', 'json'
    data_range: exportData.dataRange,
    section: exportData.section,
  });
};

/**
 * Track admin review completed
 * @param {Object} reviewData - Review information
 */
export const trackAdminReviewCompleted = (reviewData) => {
  mixpanelService.track(MIXPANEL_EVENTS.ADMIN_REVIEW_COMPLETED, {
    action: reviewData.action, // 'approved', 'flagged', 'dismissed'
    category: reviewData.category,
    review_time_seconds: reviewData.reviewTimeSeconds,
  });
};

// ============================================
// Consent Events
// ============================================

/**
 * Track consent accepted (opt-in via the AnalyticsOptOut control)
 * @param {Object} consentData - Consent acceptance information
 */
export const trackConsentAccepted = (consentData) => {
  mixpanelService.optIn();
  mixpanelService.track(MIXPANEL_EVENTS.CONSENT_ACCEPTED, {
    version: consentData.version,
    trigger: consentData.trigger,
  });
};

/**
 * Track consent declined (opt-out via the AnalyticsOptOut control)
 * @param {Object} consentData - Consent decline information
 */
export const trackConsentDeclined = (consentData) => {
  // Track before opting out
  mixpanelService.track(MIXPANEL_EVENTS.CONSENT_DECLINED, {
    version: consentData.version,
    trigger: consentData.trigger,
  });
  mixpanelService.optOut();
};

// ============================================
// Error Events
// ============================================

/**
 * Track error occurred
 * @param {Object} errorData - Error information
 */
export const trackErrorOccurred = (errorData) => {
  mixpanelService.track(MIXPANEL_EVENTS.ERROR_OCCURRED, {
    error_type: errorData.type,
    error_code: errorData.code,
    component: errorData.component,
    active_flow: errorData.activeFlow,
    is_recoverable: errorData.isRecoverable || false,
  });
};

/**
 * Track API timeout
 * @param {Object} timeoutData - Timeout information
 */
export const trackAPITimeout = (timeoutData) => {
  mixpanelService.track(MIXPANEL_EVENTS.API_TIMEOUT, {
    endpoint: timeoutData.endpoint,
    timeout_duration_ms: timeoutData.duration,
    retry_count: timeoutData.retryCount || 0,
  });
};

/**
 * Track offline fallback used
 * @param {Object} fallbackData - Fallback information
 */
export const trackOfflineFallback = (fallbackData) => {
  mixpanelService.track(MIXPANEL_EVENTS.OFFLINE_FALLBACK, {
    trigger: fallbackData.trigger, // 'network_error', 'api_timeout', 'rate_limit'
    active_flow: fallbackData.activeFlow,
    language: fallbackData.language,
  });
};

// ============================================
// Helper Functions
// ============================================

/**
 * Detect device type from user agent
 * @returns {string} - Device type
 */
function detectDeviceType() {
  if (typeof window === 'undefined') return 'unknown';

  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

/**
 * Mark user as deep explorer (called after 3+ explore more clicks)
 */
export const markAsDeepExplorer = () => {
  mixpanelService.setUserProperties({
    [USER_PROPERTIES.IS_DEEP_EXPLORER]: true,
  });
};
